import { useQuery, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { authService } from '../lib/authService';
import { SubcontractorDetail } from './datarefs.types';
import { Agent, Company, Licence } from '../components/BConnect/Types';
import { ModuleFamilyType } from '../components/Extractor/Clients/ClientsForm';
import { ExocetFamilyType, ExocetRadioModeType } from '../components/Extractor/Equipments/Types';
import { Job } from '../components/Extractor/Jobs/types/types';
import {
  DefaultRadioConfig,
  RadioConfig
} from '../components/Extractor/Equipments/Configuration/types';
import { User, UserListItem } from '../components/Admin/Users/types';
import { FrameType } from '../components/BReader/BOE/Tours/TourForm/types';
import { BREADER_API, TourFilterOptions } from '../components/BReader/customers';
import { PERIMETER_CUSTOMERS_ENDPOINT, PerimeterCustomer } from '../lib/perimeter';
import { ClientOption } from '../components/BConnect/Interventions/NewIntervention/NewInterventionForm';
import {
  SharedPerimeter,
  SharedPerimeterListing
} from '../components/Admin/SharedPerimeters/types';
import { CITIES_ENDPOINT, CityDetail, Country } from '../components/Admin/Cities/types';
import { Drive, File, Folder } from '../components/DMS/DMSTypes';
import { ApiVersion } from '../components/Sidebar/types';
import {
  DeploymentProjectDetail,
  DeploymentProjectItem,
  DeploymentProjectsIndicators,
  ProjectManager
} from '../components/CR/Deployment/type';
import { SourceApp } from '../components/Extractor/ExportFormats/sourceApp';

type Subcontractors = {
  [key: string]: any;
};

export type ApiServiceErr = any;

export const defaultSize = 9999;

type BasicType = {
  id: number;
  created_at: number;
  updated_at: number;
};

export const useGetSubcontractors = () =>
  useQuery<Subcontractors[], ApiServiceErr>({
    queryKey: ['subcontractors'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/subcontractors/subcontractors/?size=${defaultSize}&ordering=name&is_active=true`
      );
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetSubcontractorsMR = () =>
  useQuery<Subcontractors[], ApiServiceErr>({
    queryKey: ['subcontractors-mr'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/subcontractors/subcontractors/?size=${defaultSize}&ordering=name&is_active=true&action_type=MR`
      );
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetSmsCommands = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['sms-commands'],
    queryFn: async () => {
      const response = await axios.get('/api/boi/params/sms-commands/');
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetExtractorBConnectClients = () =>
  useQuery<{ name: string; bconnect_id: string }[], ApiServiceErr>({
    queryKey: ['extractor-bconnect-clients'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/bconnect-clients/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetSubcontractorsCR = () =>
  useQuery<Subcontractors[], ApiServiceErr>({
    queryKey: ['subcontractors-cr'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/subcontractors/subcontractors/?action_type=CR&size=999`
      );
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetInterventionEvents = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['intervention-events'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/params/intervention-events/?size=${defaultSize}&ordering=label`
      );
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetInterventionStatuses = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['intervention-statuses'],
    queryFn: async () => {
      const endpoint = authService.getEndpoint(
        `/api/boi/params/intervention-statuses/`,
        `/api/boe/params/intervention-statuses/`
      );
      const response = await axios.get(`${endpoint}?size=${defaultSize}&ordering=order`);
      return response.data.results;
    }
  });

export const useGetCaseEvents = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['case-events'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/params/case-events/?size=${defaultSize}&ordering=label`
      );
      return response.data.results;
    }
  });

export const useGetCaseStatuses = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['case-statuses'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/params/case-statuses/?size=${defaultSize}&ordering=order`
      );
      return response.data.results;
    }
  });

export const useGetCaseTypes = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['case-types'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/params/case-types/?size=${defaultSize}&ordering=label`
      );
      return response.data.results;
    }
  });

export const useGetContracts = (endpoint?: string) =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['contracts'],
    queryFn: async () => {
      const response = await axios.get(
        endpoint || `/api/boi/params/contracts/?size=${defaultSize}`
      );
      return response.data.results || response.data;
    }
  });

export const useGetCustomers = (endpoint?: string) =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await axios.get(
        endpoint || `/api/boi/params/customers/?size=${defaultSize}`
      );
      type Client = {
        oss_code: string;
        id: number;
      };
      type Option = {
        label: string;
        value: number;
      };
      const buildOptionsWithUniqueLabels = (clients: Client[]): Option[] => {
        const counts: Record<string, number> = {};
        const occurrences: Record<string, number> = {};

        // 1️⃣ Compter les oss_code (trim pour éviter faux doublons)
        clients.forEach((client) => {
          const key = client.oss_code.trim();
          counts[key] = (counts[key] || 0) + 1;
        });

        // 2️⃣ Construire les options avec numérotation si doublon
        return clients.map((client) => {
          const trimmed = client.oss_code.trim();

          if (counts[trimmed] > 1) {
            occurrences[trimmed] = (occurrences[trimmed] || 0) + 1;
            return {
              label: `${trimmed} (${occurrences[trimmed]})`,
              value: client.id
            };
          }

          return {
            label: trimmed,
            value: client.id
          };
        });
      };
      const filteredResults = buildOptionsWithUniqueLabels(
        response.data.results.filter((client: Client) => client.oss_code)
      ).sort();
      return filteredResults;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false
  });

export const useGetFailureCodes = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['failure-codes'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/params/failure-codes/?size=${defaultSize}&ordering=label`
      );
      return response.data.results;
    }
  });

export const useGetMessagesTypes = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['messages-types'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/administration/messages-type/?size=${defaultSize}&ordering=label&is_active=true`
      );
      return response.data;
    }
  });

export const useGetTechnicians = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['technicians'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/subcontractors/technicians/?size=${defaultSize}&ordering=user__name`
      );
      return response.data.results;
    }
  });

export const useGetGroups = () =>
  useQuery<{ name: string; id: number }[], ApiServiceErr>({
    queryKey: ['groups'],
    queryFn: async () => {
      const endpoint = authService.getEndpoint(
        `/api/boi/administration/groups/`,
        `/api/boe/subcontractors/groups/`
      );
      const response = await axios.get(
        `${endpoint}?size=${defaultSize}&ordering=name&is_active=true`
      );
      return response.data.results;
    },
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

export const useGetYears = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['years'],
    queryFn: async () => {
      const endpoint = authService.getEndpoint(`/api/boi/params/years/`, `/api/boe/params/years/`);
      const response = await axios.get(`${endpoint}?size=${defaultSize}&ordering=label`);
      return response.data.results;
    }
  });

export const useGetMonths = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['months'],
    queryFn: async () => {
      const endpoint = authService.getEndpoint(
        `/api/boi/params/months/`,
        `/api/boe/params/months/`
      );
      const response = await axios.get(`${endpoint}?size=${defaultSize}&ordering=id`);
      return response.data.results;
    }
  });

export const useGetUsers = (enableFetch = true) =>
  useQuery<User[], ApiServiceErr>({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/administration/users/?size=${defaultSize}&ordering=name&is_active=true`
      );
      return response.data.results;
    },
    enabled: authService.canAccessBOI() && enableFetch,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

export const useGetUsersList = () =>
  useQuery<UserListItem[], ApiServiceErr>({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await axios.get(`/api/lists/users/`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false
  });

export const useGetPermissions = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['permissions'],
    queryFn: async () => {
      // L'API expose toujours cette ressource sous le nom "sections" côté back
      const response = await axios.get(
        `/api/boi/administration/sections/?size=${defaultSize}&ordering=name&is_active=true`
      );
      return response.data.results;
    }
  });

export const useGetMRInterventionTypes = () =>
  useQuery<(BasicType & { code: string; label: string })[], ApiServiceErr>({
    queryKey: ['intervention_type'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/params/mr-intervention-type/?size=${defaultSize}&ordering=id`
      );
      return response.data.results;
    }
  });

export const useGetMRDeviceRequests = () =>
  useQuery<(BasicType & { clause_where: string; label: string; type: number })[], ApiServiceErr>({
    queryKey: ['mr-devices-request'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/params/mr-devices-request`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetCrCities = () =>
  useQuery<string[], ApiServiceErr>({
    queryKey: ['cr-cities'],
    queryFn: async () => {
      const endpoint = authService.getEndpoint(
        `/api/boi/cases/cr-cases/cities/`,
        `/api/boe/cases/cr-interventions-details/cities/`
      );
      const response = await axios.get(`${endpoint}`);
      return response.data;
    }
  });

export const useGetFrameTypes = () =>
  useQuery<FrameType[], ApiServiceErr>({
    queryKey: ['frameTypes'],
    queryFn: async () => {
      const response = await axios.get(`${BREADER_API}/frame-types/`);
      return response.data.results;
    },
    refetchOnWindowFocus: false
  });

export const useGetCrContracts = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['cr-contracts'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/cases/cr-cases/contracts`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetCrDeploymentProjects = (
  filterValues: {
    contract?: number;
    has_project?: boolean;
    project_manager?: number;
  } = {}
) =>
  useQuery<DeploymentProjectItem[], ApiServiceErr>({
    queryKey: ['cr-deployment-projects', filterValues],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filterValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
      const queryString = params.toString();
      const response = await axios.get(
        `/api/cr/deployment/projects${queryString ? `?${queryString}` : ''}`
      );
      return response.data.results;
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false
  });

/**
 * Détail d'un projet de déploiement CR : porte les champs absents de la liste
 * (CSM, dossier GED, updated_at). Indexé sur l'id du contrat, comme le PUT de
 * modification. L'id vient soit d'une ligne de la liste (nombre), soit de l'URL
 * (chaîne) ; absent, il désactive la requête.
 */
export const useGetCrDeploymentProject = (contractId: number | string | null | undefined) =>
  useQuery<DeploymentProjectDetail, ApiServiceErr>({
    queryKey: ['cr-deployment-project', contractId],
    queryFn: async () => {
      const response = await axios.get(`/api/cr/deployment/projects/${contractId}`);
      return response.data;
    },
    enabled: authService.canAccessBOI() && contractId !== null && contractId !== undefined,
    refetchOnWindowFocus: false
  });

/**
 * Indicateurs de la liste des contrats / projets de déploiement CR.
 * `filterString` est la chaîne de filtres produite par BSearchForm (`&customer=1&contract=2`),
 * afin que les indicateurs suivent exactement le périmètre affiché dans le tableau.
 */
export const useGetCrDeploymentProjectsIndicators = (filterString = '') =>
  useQuery<DeploymentProjectsIndicators, ApiServiceErr>({
    queryKey: ['cr-deployment-projects-indicators', filterString],
    queryFn: async () => {
      const response = await axios.get(
        `/api/cr/deployment/projects/indicators${filterString.replace('&', '?')}`
      );
      return response.data;
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false
  });

export const useGetCrDeploymentProjectManagers = () =>
  useQuery<ProjectManager[], ApiServiceErr>({
    queryKey: ['cr-deployment-project-managers'],
    queryFn: async () => {
      const response = await axios.get(`/api/cr/deployment/project-managers`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

export const useGetSubcontractorDetails = () =>
  useQuery<SubcontractorDetail, ApiServiceErr>({
    queryKey: ['subcontractors-boe'],
    queryFn: async () => {
      const response = await axios.get(`/api/boe/subcontractors/subcontractors/details/`);
      return response.data;
    },
    enabled: authService.canAccessBOE()
  });

export const useGetModuleFamilies = () =>
  useQuery<Array<ModuleFamilyType>, ApiServiceErr>({
    queryKey: ['module-families'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/module-families/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetAnomalyProfiles = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['anomaly-profiles'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/anomaly-profiles/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetErps = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['erps'],
    queryFn: async () => {
      const response = await axios.get(`/api/lists/erps/`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetAnomalyRules = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['anomaly-rules'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/params/anomaly-rules/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetClients = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['clients'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/clients/?size=999`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export type ExportFormatListItem = {
  id: number;
  name: string;
  app: SourceApp;
};

// `app` est exposé par l'endpoint de listing (APPSUPPORT-2438) : inutile de passer par
// `/api/boi/extractor/export-formats/`, qui sérialise en plus toutes les colonnes
// paramétrées de chaque format et compte ses jobs un par un.
export const useGetExportFormats = () =>
  useQuery<ExportFormatListItem[], ApiServiceErr>({
    queryKey: ['export-formats'],
    queryFn: async () => {
      const response = await axios.get(`/api/lists/export-formats/`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetJobs = (): UseQueryResult<Job[], ApiServiceErr> =>
  useQuery<Job[], ApiServiceErr>({
    queryKey: ['extractor-jobs'],
    queryFn: async () => {
      const response = await axios.get<{ results: Job[] }>(
        `/api/boi/extractor/jobs/?size=${defaultSize}&ordering=name`
      );
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

// Chaque menu de paramétrage n'expose que les sources qui le concernent : la requête
// attend donc de connaître l'application du format avant de partir.
export const useGetExtractorFields = (app: SourceApp | undefined) =>
  useQuery<Array<{ name: string; type: string; id: number; source: string }>, ApiServiceErr>({
    queryKey: ['extractor-fields', app],
    queryFn: async () => {
      const response = await axios.get(
        app === 'BSTOCK' ? `/api/boi/extractor/fields-bstock/` : `/api/boi/extractor/fields/`
      );
      return response.data;
    },
    enabled: authService.canAccessBOI() && Boolean(app)
  });

// Les libellés sont traduits côté API (Session.OperationType.choices).
export const useGetSessionTypes = () =>
  useQuery<{ value: string; label: string }[], ApiServiceErr>({
    queryKey: ['session-types'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/session-type/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetDataMapping = () => {
  return useQuery<any[], ApiServiceErr>({
    queryKey: ['data-mapping'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/data-mapping/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });
};

export const useGetInterventionTypes = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['intervention-types'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/intervention-types/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetBConnectInterventionTypes = () =>
  useQuery<{ Id: number; Name: string }[], ApiServiceErr>({
    queryKey: ['b-connect-intervention-types'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/exocet/bconnect-interventions-types/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetRadioModes = () =>
  useQuery<{ value: number | null; label: string }[], ApiServiceErr>({
    queryKey: ['b-connect-radio-modes'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/exocet/bconnect-radio-modes/`);
      const results = response.data.map((item: any) => {
        return { value: item.Id, label: item.Label };
      });
      const byDefault = { value: null, label: 'Par défaut' };
      const newResults = [byDefault, ...results];

      return newResults;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetBConnectInterventionsEvents = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['interventions-events'],
    queryFn: async () => {
      const response = await axios.get('/api/boi/params/bconnect-interventions-events/?size=100');
      const events = response.data.results;

      const formatEvents = events.map((event: any) => {
        return { value: event.label, label: event.label };
      });

      const eventsListSorted = formatEvents.sort((a: any, b: any) =>
        a.label.localeCompare(b.label)
      );
      return eventsListSorted;
    }
  });

//bconnect
export const useGetCompanies = () =>
  useQuery<Company[], ApiServiceErr>({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/bconnect/webapp/administrator/companies?ordering=Id__Name&size=999`
      );
      return response.data.results;
    }
  });

const perimeterCustomersQuery = (limit?: number) => ({
  queryKey: limit ? ['perimeter-customers', { limit }] : ['perimeter-customers'],
  queryFn: async (): Promise<PerimeterCustomer[]> => {
    const response = await axios.get(PERIMETER_CUSTOMERS_ENDPOINT, { params: { limit } });
    return response.data;
  },
  refetchOnWindowFocus: false
});

/** The first `limit` customers of the perimeter (all of them without a limit), ordered by OSS code. */
export const useGetPerimeterCustomers = ({ limit }: { limit?: number } = {}): UseQueryResult<
  PerimeterCustomer[],
  ApiServiceErr
> => useQuery<PerimeterCustomer[], ApiServiceErr>(perimeterCustomersQuery(limit));

/** All the perimeter customers as select options; shares the cache of useGetPerimeterCustomers(). */
export const useGetClientOssCode = (): UseQueryResult<ClientOption[], ApiServiceErr> =>
  useQuery<PerimeterCustomer[], ApiServiceErr, ClientOption[]>({
    ...perimeterCustomersQuery(),
    select: (customers) =>
      customers.map((customer) => ({ label: customer.oss_code, value: customer.id })),
    enabled: authService.canAccessBOI()
  });

export const useGetAgents = () =>
  useQuery<Agent[], ApiServiceErr>({
    queryKey: ['bconnect-agents'],
    queryFn: async () => {
      const response = await axios.get(
        `/api/bconnect/webapp/customer/agents/?ordering=Id__Name&size=999999`
      );
      return response.data.results;
    }
  });

export const useGetBConnectAgents = () =>
  useQuery<{ name: string }[], ApiServiceErr>({
    queryKey: ['extractor-agents'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/agents/?size=99999`);
      return response.data.results;
    }
  });

export const useGetBConnectClients = () =>
  useQuery<{ Name: string; Id: number }[], ApiServiceErr>({
    queryKey: ['b-connect_clients'],
    queryFn: async () => {
      const response = await axios.get('/api/boi/exocet/bconnect-clients/?size=999');
      return response.data.results;
    }
  });

export const useGetLicences = () =>
  useQuery<Licence[], ApiServiceErr>({
    queryKey: ['licences'],
    queryFn: async () => {
      const response = await axios.get('/api/bconnect/webapp/customer/agents_stats');
      return response.data.results;
    }
  });

export const useGetFP2EBrandsDropDown = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['list-of-pf2e-brands'],
    queryFn: async () => {
      const response = await axios.get(
        '/api/bconnect/webapp/administrator/references/fp2e-sensor-brands'
      );
      const data = response.data;
      const results = data.map((brand: any) => {
        return { label: `${brand.Label} (Code ${brand.Code})`, value: brand.Id };
      });
      return results;
    }
  });

export const useGetFP2EDiametersDropDown = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['list-of-pf2e-diameters'],
    queryFn: async () => {
      const response = await axios.get(
        '/api/bconnect/webapp/administrator/references/fp2e-diameter'
      );
      const data = response.data;
      const results = data.map((diam: any) => {
        return {
          label: `${diam.Value} (Code ${diam.Code}/Clé métier ${diam.MeterKey})`,
          value: diam.Id
        };
      });
      return results;
    }
  });

//extractor
export const useGetCities = (endpoint?: string) =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['cities'],
    queryFn: async () => {
      const response = await axios.get(endpoint ?? `/api/boi/extractor/interventions/cities/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetExtractorInterventionStatuses = () =>
  useQuery<any, ApiServiceErr>({
    queryKey: ['bconnect-interventions-status'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/interventions-status/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

//bstock
export const useGetProtocols = () =>
  useQuery<{ id: number; label: string }[], ApiServiceErr>({
    queryKey: ['protocols'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/params/protocols/`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetBStockDeviceTypes = () =>
  useQuery<{ id: number; label: string }[], ApiServiceErr>({
    queryKey: ['bstock-device-types'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/params/bstock-device-types/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetFabcompDeviceTypes = () =>
  useQuery<string[], ApiServiceErr>({
    queryKey: ['fabcomp-device-types'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/params/fabcomp-configurations/device-types/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetAgentsString = () =>
  useQuery<string[], ApiServiceErr>({
    queryKey: ['agents-string'],
    queryFn: async () => {
      const response = await axios.get(`/api/bstock/sessions/agents/`);
      return response.data;
    }
  });

//breader
/** Readers a tour of this customer can be assigned to (MOB_BREADER users having the customer in their perimeter). */
export const useGetBReaderReaders = (
  customer: number | null | undefined
): UseQueryResult<{ id: number; name: string }[], ApiServiceErr> =>
  useQuery<{ id: number; name: string }[], ApiServiceErr>({
    queryKey: ['breader-readers', customer],
    queryFn: async () => {
      const response = await axios.get(`${BREADER_API}/readers/`, {
        params: { customer, size: 999 }
      });
      return response.data.results;
    },
    enabled: !!customer,
    refetchOnWindowFocus: false
  });

export const useGetTourFilterOptions = (filters: {
  customer?: number;
  reader?: number;
  city?: string;
}): UseQueryResult<TourFilterOptions, ApiServiceErr> =>
  useQuery<TourFilterOptions, ApiServiceErr>({
    queryKey: ['breader-tour-filter-options', filters],
    queryFn: async () => {
      const response = await axios.get(`${BREADER_API}/tours/filter-options/`, { params: filters });
      return response.data;
    },
    refetchOnWindowFocus: false
  });

//exocet
export const useGetExocetFamilies = () =>
  useQuery<Array<ExocetFamilyType>, ApiServiceErr>({
    queryKey: ['exocet-families'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/exocet/bconnect-families/?size=999`);
      return response.data;
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  });

export const useGetExocetRadioModes = () =>
  useQuery<Array<ExocetRadioModeType>, ApiServiceErr>({
    queryKey: ['radio-modes'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/exocet/bconnect-radio-modes/`);
      return response.data;
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  });

export const useGetBConnectContracts = () =>
  useQuery<{ Id: number; Name: string }[], ApiServiceErr>({
    queryKey: ['bconnect-contracts'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/exocet/bconnect-contracts/`);
      return response.data;
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  });

export const useGetClientsAndContracts = (search?: string, oss_codes?: string[]) => {
  return useQuery<
    Array<{
      id: number;
      oss_code: string;
      contracts: { id: number; oss_code: string; oss_label: string }[];
    }>,
    ApiServiceErr
  >({
    queryKey: ['clients-and-contracts', search, oss_codes],
    queryFn: async () => {
      const endpoint = `/api/boi/params/customers-and-contracts/${search ? `?search=${search}&size=999` : oss_codes ? `?oss_code__in=${oss_codes.join(',')}` : '?size=999'}`;
      const response = await axios.get(endpoint);
      return response.data.results || [];
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  });
};

export const useGetExocetClients = ({
  search,
  oss_codes
}: {
  search?: string;
  oss_codes?: string[];
}) => {
  return useQuery<
    Array<{
      id: number;
      oss_code: string;
      contracts: { id: number; oss_code: string; oss_label: string }[];
    }>,
    ApiServiceErr
  >({
    queryKey: ['exocet-clients', search, oss_codes],
    queryFn: async () => {
      const endpoint = `/api/boi/exocet/clients/${search ? `?search=${search}&size=999` : oss_codes ? `?oss_code__in=${oss_codes.join(',')}` : '?size=999'}`;
      const response = await axios.get(endpoint);
      return response.data.results || [];
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false
    // staleTime: 5 * 60 * 1000
  });
};

export const useGetExocetClientsByCode = (search?: string) => {
  return useQuery<
    Array<{
      id: number;
      oss_code: string;
      contracts: { id: number; oss_code: string; oss_label: string }[];
    }>,
    ApiServiceErr
  >({
    queryKey: ['exocet-clients', search],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/exocet/clients${search ? `?search=${search}&size=999` : ''}`
      );
      return response.data.results || [];
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false
    // staleTime: 5 * 60 * 1000
  });
};

// La valeur des options est l'id du contrat: c'est ce que les filtres de l'API
// attendent (contract_id__in), le oss_code n'étant qu'un libellé d'affichage.
export const useGetExocetClientContracts = (client: string) =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['exocet-client-contracts', client],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/exocet/select-customer-contract/?customer_id=${client}&size=999`
      );
      const results = response.data;
      const contracts = results.map((item: any) => {
        return { label: item.oss_code, value: item.id };
      });
      return contracts;
    },
    enabled: authService.canAccessBOI() && !!client,
    refetchOnWindowFocus: false
  });

export const useGetExocetClientCities = (client: string, baseUrl?: string) =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['exocet-client-cities', client, baseUrl],
    queryFn: async () => {
      const url = baseUrl ?? '/api/boi/exocet/select-customer-cities/';
      const response = await axios.get(`${url}?customer_id=${client}&size=999`);
      const results = response.data;
      const cities = results.map((item: any) => {
        return { label: item.label, value: item.id };
      });
      return cities;
    },
    enabled: authService.canAccessBOI() && !!client,
    refetchOnWindowFocus: false
  });

export const useGetExocetClientStreets = (client: string, city?: string, baseUrl?: string) =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['exocet-client-streets', client, city, baseUrl],
    queryFn: async () => {
      const url = city
        ? `${baseUrl}?customer_id=${client}&city_id=${city}`
        : `${baseUrl}?customer_id=${client}`;
      const response = await axios.get(url);
      const results = response.data.map((item: string) => {
        return { label: item, value: item };
      });
      return results;
    },
    enabled: authService.canAccessBOI() && !!client,
    refetchOnWindowFocus: false
  });

export const useGetExocetDiameters = (client: string) =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['exocet-client-diameters', client],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/exocet/bconnect-diameters/`);
      const results = response.data.map((item: any) => {
        return { label: item.Value, value: item.Id };
      });
      return results;
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false
  });

export const useGetExocetParents = (client: string) =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['exocet-client-parents', client],
    queryFn: async () => {
      const response = await axios.get(
        `/api/boi/exocet/pds/qualify-parents/?customer_id=${client}`
      );
      const results = response.data.map((item: string) => {
        return { label: item, value: item };
      });
      return results;
    },
    enabled: authService.canAccessBOI() && !!client,
    refetchOnWindowFocus: false
  });

export const useGetExocetTechnicians = (client: string) =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['exocet-technicians', client],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/exocet/client-technicians/?customer_id=${client}`);
      const results = response.data.map((item: any) => {
        return { label: item.name, value: item.id };
      });
      return results;
    },
    enabled: authService.canAccessBOI() && !!client,
    refetchOnWindowFocus: false
  });

export const useGetExocetPdsToSelect = (
  client: string,
  lastPdsImport: Date | number,
  filterValues: {
    contract_id__in?: any[];
    city_id__in?: any[];
    device_id?: string;
  }
) => {
  return useQuery<{ results: any[]; count: number }, ApiServiceErr>({
    queryKey: ['exocet-pds-to-select', client, lastPdsImport, filterValues],
    queryFn: async () => {
      const params = new URLSearchParams({ size: '9999' });
      Object.entries(filterValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
      const response = await axios.get(
        `/api/boi/exocet/pds/to-select/?customer_id=${client}&${params.toString()}`
      );
      return response.data;
    },
    enabled: authService.canAccessBOI() && !!client,
    refetchOnWindowFocus: false
  });
};

export const useGetConfigurations = (
  client: string,
  // Noms alignés sur ce que le code envoie réellement : le commit fca47270 a renommé
  // target_radio_mode / target_specific_criteria en radio_mode / target_criteria côté
  // composant sans mettre à jour cette signature, qui ne protégeait donc plus rien.
  filterValues: {
    dst?: boolean | null;
    autoconnect?: boolean | null;
    radio_mode?: number | null;
    target_criteria?: boolean | null;
  }
) => {
  return useQuery<{ value: any; label: any }[], ApiServiceErr>({
    queryKey: ['exocet-configurations-filters', client, filterValues],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filterValues).forEach(([key, value]) => {
        // `false` doit partir, contrairement à `null` : le critère cible est un tri-état.
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
      const response = await axios.get(
        `/api/boi/exocet/configurations/?customer_id=${client}&${params.toString()}`
      );
      const results = response.data.map((item: any) => {
        return { value: item.id, label: item.name };
      });
      const byDefault = { value: null, label: 'Par défaut' };
      const newResults = [byDefault, ...results];

      return newResults;
    },
    enabled: authService.canAccessBOI() && !!client,
    refetchOnWindowFocus: false
  });
};

export const useGetExocetPdsToQualify = (
  client: string,
  lastPdsImport: Date | number,
  filterValues: {
    in_progress?: boolean;
    contract_id__in?: any[];
    city_id__in?: any[];
    address_street_name__in?: any[];
    sensor_family_type__in?: any[];
    equipment?: string;
    equipment_diameter__in?: any[];
    parent?: any[];
    /** Recherche libre de la v2, portée par la barre d'outils de l'étape. */
    search?: string;
  }
) => {
  return useQuery<{ results: any[]; count: number }, ApiServiceErr>({
    queryKey: ['exocet-pds-to-qualify', client, lastPdsImport, filterValues],
    queryFn: async () => {
      const params = new URLSearchParams({ size: '9999' });
      Object.entries(filterValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
      const response = await axios.get(
        `/api/boi/exocet/pds/qualify/?customer_id=${client}&${params.toString()}`
      );

      return response.data;
    },
    enabled: authService.canAccessBOI() && !!client,
    refetchOnWindowFocus: false
  });
};

export const useGetExocetPdsToSimpleAssign = (
  client: string,
  lastPdsImport: Date | number,
  filterValues: {
    in_progress?: boolean;
    contract_id__in?: any[];
    city_id__in?: any[];
    address_street_name__in?: any[];
    sensor_family_type__in?: any[];
    equipment?: string;
    /** Recherche libre de la v2, portée par la barre d'outils de l'étape. */
    search?: string;
  }
) => {
  return useQuery<{ results: any[]; count: number }, ApiServiceErr>({
    queryKey: ['exocet-pds-to-simple-assign', client, lastPdsImport, filterValues],
    queryFn: async () => {
      const params = new URLSearchParams({ size: '9999' });
      Object.entries(filterValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
      const response = await axios.get(
        `/api/boi/exocet/pds/assign-technician/?customer_id=${client}&${params.toString()}`
      );

      return response.data;
    },
    enabled: authService.canAccessBOI() && !!client,
    refetchOnWindowFocus: false
  });
};

export const useGetExocetConfigurations = () =>
  useQuery<Array<RadioConfig>, ApiServiceErr>({
    queryKey: ['exocet-configurations'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/params/configuration-profiles/?size=999`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false
  });

export const useGetExocetDefaultConfigurations = () =>
  useQuery<Array<DefaultRadioConfig>, ApiServiceErr>({
    queryKey: ['exocet-default-configurations'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/params/default-configurations/?size=999`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI(),
    refetchOnWindowFocus: false
  });

export const useGetExocetInterventionsStatuses = () =>
  useQuery<Array<ExocetRadioModeType>, ApiServiceErr>({
    queryKey: ['exocet-interventions-statuses'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/exocet/bconnect-interventions-status/`);
      return response.data;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetMedia = (id: number) =>
  useQuery<Array<any>, ApiServiceErr>({
    queryKey: ['media'],
    queryFn: async () => {
      const response = await axios.get(`/api/media/${id}/`);
      return response.data.results;
    }
  });

export const useGetMediaFolders = () =>
  useQuery<
    Array<{
      id: number;
      label: string;
      created_at: number;
      updated_at: number;
    }>,
    ApiServiceErr
  >({
    queryKey: ['media-folders'],
    queryFn: async () => {
      const endpoint = authService.getEndpoint(
        `/api/boi/params/media-folders/`,
        `/api/boe/params/media-folders/`
      );
      const response = await axios.get(`${endpoint}?size=${defaultSize}`);
      const res = response.data?.results || [];
      return res;
    }
  });

export const useGetSharedPerimeters = (filters = '') =>
  useQuery<SharedPerimeter[], ApiServiceErr>(
    ['shared-perimeters', filters],
    async () => {
      const response = await axios.get(
        `/api/boi/params/shared-perimeters/?ordering=name${filters}`
      );
      return response.data.results;
    },
    { enabled: authService.canAccessBOI() }
  );

export const useGetSharedPerimetersForListing = () =>
  useQuery<SharedPerimeterListing[], ApiServiceErr>(
    ['shared-perimeters-for-listing'],
    async () => {
      const response = await axios.get(`/api/lists/shared-perimeters/`);
      const results = response.data.results.map((item: any) => {
        return { value: item.id, label: item.name };
      });
      return results;
    },
    { enabled: authService.canAccessBOI(), refetchOnWindowFocus: false }
  );

export const useGetCountries = (): UseQueryResult<Country[], ApiServiceErr> =>
  useQuery<Country[], ApiServiceErr>({
    queryKey: ['countries'],
    queryFn: async () => {
      const response = await axios.get<{ results: Country[] }>(`/api/lists/countries/`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI(),
    staleTime: 1000 * 60 * 60, // 1 heure : liste de référence quasi-statique
    refetchOnWindowFocus: false
  });

export const useGetCity = (cityId: number | null): UseQueryResult<CityDetail, ApiServiceErr> =>
  useQuery<CityDetail, ApiServiceErr>({
    queryKey: ['city', cityId],
    queryFn: async () => {
      const response = await axios.get<CityDetail>(`${CITIES_ENDPOINT}${cityId}/`);
      return response.data;
    },
    enabled: cityId !== null && authService.canAccessBOI()
  });

export const useGetSharedPerimeterDetail = (id?: string | number | null) =>
  useQuery<SharedPerimeter, ApiServiceErr>(
    ['shared-perimeter-detail', id],
    async () => {
      const response = await axios.get(`/api/boi/params/shared-perimeters/${id}/`);
      return response.data;
    },
    {
      enabled: authService.canAccessBOI() && id !== undefined,
      refetchOnWindowFocus: false
    }
  );

export const useGetDrives = () =>
  useQuery<Drive[], ApiServiceErr>({
    queryKey: ['drives'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/params/drives/?ordering=region&size=99999`);
      return response.data.results;
    }
  });

export const useGetDMSItems = (selectedDriveId: string) =>
  useQuery<{ folders: Folder[]; files: File[] }, ApiServiceErr>({
    queryKey: ['dms-items', selectedDriveId],
    queryFn: async () => {
      if (authService.canAccessBOI()) {
        const response = await axios.get(`/api/boi/dms/list-files/?folder_id=${selectedDriveId}`);
        return response.data;
      } else {
        const response = await axios.get('/api/boe/dms/list-allowed-folders/');
        return { folders: response.data.folders, files: [] };
      }
    },
    enabled: authService.canAccessBOI() ? !!selectedDriveId : authService.isViewerGED()
  });

export const useGetApiVersion = () =>
  useQuery<ApiVersion, ApiServiceErr>({
    queryKey: ['api-version'],
    queryFn: async () => {
      const response = await axios.get('/api/version/');
      return response.data;
    }
  });

export const useGetInterventionPricing = (interventionId: number | undefined) => {
  const endpoint = authService.getEndpoint(
    `/api/boi/cases/technician-feedback/${interventionId}/`,
    `/api/boe/cases/cr-interventions-details/${interventionId}/`
  );
  return useQuery<any, ApiServiceErr>({
    queryKey: ['intervention-pricing', interventionId],
    queryFn: async () => {
      const response = await axios.get(endpoint);
      return response.data;
    },
    enabled: !!interventionId
  });
};

export const useGetSubcontractorById = (subcontractorId: number | undefined) => {
  const endpoint = authService.getEndpoint(
    `/api/boi/subcontractors/subcontractors/${subcontractorId}/`,
    `/api/boe/subcontractors/subcontractors/details/`
  );
  return useQuery<any, ApiServiceErr>({
    queryKey: ['subcontractor-by-id', subcontractorId],
    queryFn: async () => {
      const response = await axios.get(endpoint);
      return response.data;
    },
    enabled: !!subcontractorId
  });
};

export const useGetInterventionContracts = () =>
  useQuery<{ id: string; label: string }[], ApiServiceErr>({
    queryKey: ['intervention-contracts-form'],
    queryFn: async () => {
      const response = await axios.get('/api/boi/extractor/interventions/contracts/');
      return response.data.map((contract: any) => ({
        id: contract.contract_name,
        label: contract.contract_name
      }));
    },
    enabled: authService.canAccessBOI()
  });

export const useGetReasons = () =>
  useQuery<{ id: number; label: string }[], ApiServiceErr>({
    queryKey: ['intervention-reasons'],
    queryFn: async () => {
      const response = await axios.get('/api/bconnect/webapp/administrator/references/reason');
      return response.data.map((reason: any) => ({ id: reason.Id, label: reason.DefaultLabel }));
    }
  });

export const useGetSupportTypes = () =>
  useQuery<{ id: number; label: string }[], ApiServiceErr>({
    queryKey: ['support-types'],
    queryFn: async () => {
      const response = await axios.get('/api/boi/extractor/references/support-type/');
      const data = response.data;
      data.sort((a: any, b: any) => a.Label.localeCompare(b.Label));
      return data.map((type: any) => ({ id: type.Id, label: type.Label }));
    },
    enabled: authService.canAccessBOI()
  });

export const useGetRal = () =>
  useQuery<{ id: number; label: string }[], ApiServiceErr>({
    queryKey: ['ral'],
    queryFn: async () => {
      const response = await axios.get('/api/boi/extractor/references/RAL/');
      const data = response.data;
      data.sort((a: any, b: any) => a.Label.localeCompare(b.Label));
      return data.map((color: any) => ({ id: color.Id, label: color.Label }));
    },
    enabled: authService.canAccessBOI()
  });

export const useGetSensorBrands = (isWaterMeter: boolean | undefined) =>
  useQuery<{ id: number; label: string; code?: string; models?: any[] }[], ApiServiceErr>({
    queryKey: ['sensor-brands', isWaterMeter],
    queryFn: async () => {
      const url = isWaterMeter
        ? '/api/boi/extractor/references/fp2e-sensor-brands/'
        : '/api/boi/extractor/references/sensor-brand/';
      const response = await axios.get(url);
      const data = response.data;
      data.sort((a: any, b: any) => a.Label.localeCompare(b.Label));
      return data.map((brand: any) => ({
        id: brand.Id,
        label: brand.Label,
        code: brand.Code,
        models: brand.Models
      }));
    },
    enabled: authService.canAccessBOI() && isWaterMeter !== undefined
  });

export const useGetExtractorCompanies = () =>
  useQuery<{ id: number; name: string }[], ApiServiceErr>({
    queryKey: ['extractor-companies'],
    queryFn: async () => {
      const response = await axios.get(`/api/boi/extractor/clients/?size=999`);
      return response.data.results;
    },
    enabled: authService.canAccessBOI()
  });

export const useGetNotationsCriterias = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['notations-criterias'],
    queryFn: async () => {
      const response = await axios.get('/api/bconnect/webapp/administrator/notations/criteria');
      return response.data;
    }
  });

export const useGetNotationsExports = () =>
  useQuery<any[], ApiServiceErr>({
    queryKey: ['notations-exports'],
    queryFn: async () => {
      const response = await axios.get('/api/bconnect/webapp/administrator/notations/export');
      return response.data;
    }
  });
